import { USER_FILTER_KEYS, type UserFilterKey } from './user-filters.js';
import type { CapabilityCode } from './capabilities.js';

export const APP_IDS = [
  'taksim-chat',
  'beurtele-chat',
  'base-chat',
  'arabic-chat',
  'maghrebtele-chat',
  'tc-fun',
  'bt-fun',
  'bc-fun',
] as const;
export type AppId = (typeof APP_IDS)[number];

export const ALL_APPS = 'all' as const;
export type ActiveAppSelection = AppId | typeof ALL_APPS;

export interface AppDescriptor {
  readonly id: AppId;
  readonly displayName: string;
  readonly accentColor: string;
  readonly capabilities: readonly CapabilityCode[];
  /**
   * App-user filter keys this app's adapter can answer. The web renders only
   * these chips; the API rejects anything outside the set with 422. An app
   * with no app-users adapter declares an empty array.
   */
  readonly supportedUserFilters: readonly UserFilterKey[];
  /**
   * The language and culture of the app's users — what synthetic names,
   * handles and bios are generated in. Declared on the fun-apps only; read it
   * through `APP_REGISTRY[appId]`, never off an app-id literal.
   */
  readonly marketLocale?: MarketLocale;
}

/** A market the synthetic generators have a locale pack for. */
export type MarketLocale = 'tr' | 'fr' | 'de';

// Note: there is intentionally no `connectionKey` field here.
// Apps have multiple data sources keyed differently per capability —
// e.g. BeurteletChat uses `btmobil` for SMS but `beurtelechat` for VIP,
// and BaseChat's VIP is an HTTP API with no DB connection at all.
// Each adapter declares the connection it needs.
//
// New-app capability arrays mirror the established TaksimChat/BeurteletChat
// surface as a starting placeholder. Adapter work will refine them.
export const APP_REGISTRY: Readonly<Record<AppId, AppDescriptor>> = Object.freeze({
  'taksim-chat': {
    id: 'taksim-chat',
    displayName: 'TaksimChat',
    accentColor: '#f59e0b',
    capabilities: ['ftu', 'sms', 'vip', 'appUsers', 'userBan', 'voiceRecords'],
    // Empty until this app's adapter actually compiles a filter bag. Its
    // `list()` ignores `query.filters` entirely, so declaring a key here would
    // let the 422 gate pass it and return every row under a total that reads
    // as filtered — the exact silent-ignore this contract exists to prevent.
    supportedUserFilters: [],
  },
  'beurtele-chat': {
    id: 'beurtele-chat',
    displayName: 'BeurteletChat',
    accentColor: '#a855f7',
    capabilities: ['ftu', 'sms', 'vip', 'appUsers', 'userBan', 'voiceRecords'],
    // See taksim-chat: adapter does not read `query.filters` yet.
    supportedUserFilters: [],
  },
  'base-chat': {
    id: 'base-chat',
    displayName: 'BaseChat',
    accentColor: '#0ea5e9',
    capabilities: ['ftu', 'otp', 'vip', 'appUsers', 'userBan', 'voiceRecords'],
    // See taksim-chat: adapter does not read `query.filters` yet.
    supportedUserFilters: [],
  },
  'arabic-chat': {
    id: 'arabic-chat',
    displayName: 'ArabicChat',
    accentColor: '#10b981',
    capabilities: ['ftu', 'sms', 'vip'],
    supportedUserFilters: [],
  },
  'maghrebtele-chat': {
    id: 'maghrebtele-chat',
    displayName: 'Maghrebtelechat',
    accentColor: '#ef4444',
    capabilities: ['ftu', 'sms', 'vip', 'userBan', 'voiceRecords'],
    supportedUserFilters: [],
  },
  'tc-fun': {
    id: 'tc-fun',
    displayName: 'TaksimChatFun',
    marketLocale: 'tr',
    accentColor: '#ec4899',
    capabilities: [
      'ftu', 'sms', 'vip', 'appUsers', 'accountType',
      'coinHistory', 'revenuecatSync', 'revenuecatConsole',
      'announcements', 'messageTemplates', 'messageAutomations',
      'avatars', 'interests', 'horoscopes', 'allowances', 'cosmetics', 'games',
      'syntheticUsers', 'shop', 'quests', 'spotlight', 'voiceRooms', 'gift',
      'callMonitoring', 'userMonitoring', 'reports', 'systemConfig', 'textFilters',
      // These two reached their app-aware call sites without ever entering a
      // capability array (ADR 0045). An unregistered code is not a no-op: it
      // fails every gate, so the photo-moderation tile rendered nothing and the
      // conference controls threw "no single active app supports" — for tc-fun
      // too, the app they were written for. `capability-gates.test.ts` in the
      // web app now fails on a gate no app declares.
      'profileImages', 'conference', 'moderationReports', 'liveopReport',
    ],
    supportedUserFilters: USER_FILTER_KEYS,
  },
  'bt-fun': {
    id: 'bt-fun',
    displayName: 'BeurteletChatFun',
    marketLocale: 'fr',
    accentColor: '#6366f1',
    // Full parity with tc-fun: every capability now dispatches through the
    // shuffly family (ADR 0045), API and web alike. The two arrays are equal on
    // purpose — a capability only enters this one once BOTH its API dispatcher
    // entry and its web call site are app-aware, and that is now true for all
    // of them. `shuffly-family.test.ts` fails if this ever declares something
    // tc-fun does not.
    capabilities: [
      'ftu', 'sms', 'vip', 'appUsers', 'accountType',
      'coinHistory', 'revenuecatSync', 'revenuecatConsole',
      'announcements', 'messageTemplates', 'messageAutomations',
      'avatars', 'interests', 'horoscopes', 'allowances', 'cosmetics', 'games',
      'syntheticUsers', 'shop', 'quests', 'spotlight', 'voiceRooms', 'gift',
      'callMonitoring', 'userMonitoring', 'reports', 'systemConfig', 'textFilters',
      'profileImages', 'conference', 'moderationReports', 'liveopReport',
    ],
    // Same adapter as tc-fun's, so the same filter bag compiles.
    supportedUserFilters: USER_FILTER_KEYS,
  },
  'bc-fun': {
    id: 'bc-fun',
    displayName: 'BaseChatFun',
    marketLocale: 'de',
    accentColor: '#14b8a6',
    // Third shuffly deployment (Germany; box imaged from tc-fun, branch cut
    // from shuffly `prod`). Same parity argument as bt-fun: every capability
    // already dispatches per deployment, so declaring the full family set is a
    // statement about the code, not about the box being ready. An unconfigured
    // BC_FUN_API_BASE_URL answers CapabilityNotSupported, not a wrong-app read.
    capabilities: [
      'ftu', 'sms', 'vip', 'appUsers', 'accountType',
      'coinHistory', 'revenuecatSync', 'revenuecatConsole',
      'announcements', 'messageTemplates', 'messageAutomations',
      'avatars', 'interests', 'horoscopes', 'allowances', 'cosmetics', 'games',
      'syntheticUsers', 'shop', 'quests', 'spotlight', 'voiceRooms', 'gift',
      'callMonitoring', 'userMonitoring', 'reports', 'systemConfig', 'textFilters',
      'profileImages', 'conference', 'moderationReports', 'liveopReport',
    ],
    supportedUserFilters: USER_FILTER_KEYS,
  },
});

export function isAppId(value: string): value is AppId {
  return (APP_IDS as readonly string[]).includes(value);
}

/**
 * Apps that run the shuffly backend codebase — TaksimChatFun,
 * BeurteletChatFun and BaseChatFun today (ADR 0045). They are separate
 * installations with separate databases; what they share is the *shape* of
 * their backend.
 *
 * The panel needs this beyond the API's adapter wiring: several web surfaces
 * are shaped by which backend answers, not by a declared capability. The
 * moderation workspace is the standing example — the shuffly apps have no
 * analysis-driven banlist, so they render the scope-ban workspace instead.
 *
 * Gate those surfaces on `isShufflyApp`, never on `appId === 'tc-fun'`: the
 * literal silently sends every later fun-app down the wrong branch.
 */
export const SHUFFLY_APP_IDS = ['tc-fun', 'bt-fun', 'bc-fun'] as const satisfies readonly AppId[];

export function isShufflyApp(value: ActiveAppSelection): boolean {
  return (SHUFFLY_APP_IDS as readonly string[]).includes(value);
}

export function resolveAppsFor(selection: ActiveAppSelection): readonly AppDescriptor[] {
  if (selection === ALL_APPS) {
    return APP_IDS.map((id) => APP_REGISTRY[id]);
  }
  return [APP_REGISTRY[selection]];
}

export function supportedUserFiltersFor(app: AppId): readonly UserFilterKey[] {
  return APP_REGISTRY[app].supportedUserFilters;
}
